import { useState } from 'react';
import { Shield, X, MessageCircle, Star, Bug } from 'lucide-react';
import { colors, spacing, radii, typography, fontWeights, shadows } from '../styles/designTokens';

/**
 * Builds a mailto: URL for a bug report with all fields encoded in the body.
 * Exported for testability (Property 9).
 *
 * @param {{ description: string, steps: string, expected: string, actual: string, additional: string }} bugReport
 * @returns {string} A mailto: URL string
 */
export const buildBugReportMailtoUrl = (bugReport) => {
  const subject = 'TravelFlow Beta Bug Report';
  const body =
    `Environment\n` +
    `- User Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}\n` +
    `- URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}\n` +
    `- Timestamp: ${new Date().toISOString()}\n\n` +
    `Description\n${bugReport.description}\n\n` +
    `Steps to Reproduce\n${bugReport.steps}\n\n` +
    `Expected Behavior\n${bugReport.expected}\n\n` +
    `Actual Behavior\n${bugReport.actual}\n\n` +
    `Additional Notes\n${bugReport.additional}`;

  return `mailto:dmilliorn@travelflow.link?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const BetaBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugReport, setBugReport] = useState({
    description: '',
    steps: '',
    expected: '',
    actual: '',
    additional: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitBugReport = () => {
    if (!bugReport.description.trim()) {
      alert('Please provide a description of the problem.');
      return;
    }
    
    setIsSubmitting(true);
    
    // Open mailto link with pre-filled bug report
    window.location.href = buildBugReportMailtoUrl(bugReport);
    
    // Reset form after a short delay
    setTimeout(() => {
      setBugReport({
        description: '',
        steps: '',
        expected: '',
        actual: '',
        additional: ''
      });
      setShowBugReport(false);
      setIsSubmitting(false);
    }, 1000);
  };

  if (!isVisible) return null;

  return (
    <>
      <div style={{
        background: colors.surfaceAlt,
        color: colors.textSecondary,
        padding: `${spacing.cardPaddingMobile} ${spacing.cardPadding}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: typography.body.fontSize,
        fontWeight: fontWeights.medium,
        boxShadow: shadows.card,
        borderLeft: `3px solid ${colors.divider}`,
        position: 'relative',
        zIndex: 1000,
        width: '100%',
        boxSizing: 'border-box',
        minHeight: '44px',
        flexShrink: 0,
        lineHeight: '1.4'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flex: 1,
          minWidth: 0
        }}>
          <Shield size={16} style={{ flexShrink: 0, color: colors.textMuted }} />
          <span style={{ 
            lineHeight: '1.4',
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
            <strong>Beta Version</strong> - Testing TravelFlow! 
            <span style={{ marginLeft: '8px', color: colors.textMuted }}>
              Share feedback to help us improve.
            </span>
          </span>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          flexShrink: 0
        }}>
          <button
            onClick={() => setShowFeedback(!showFeedback)}
            style={{
              background: colors.surface,
              border: `1px solid ${colors.divider}`,
              color: colors.textSecondary,
              padding: '4px 10px',
              borderRadius: radii.inner,
              fontSize: typography.caption.fontSize,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = colors.surfaceAlt}
            onMouseOut={(e) => e.target.style.background = colors.surface}
          >
            <MessageCircle size={14} />
            Feedback
          </button>
          
          <button
            onClick={() => setIsVisible(false)}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = colors.divider}
            onMouseOut={(e) => e.target.style.background = 'none'}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Feedback Panel */}
      {showFeedback && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          position: 'relative',
          zIndex: 999,
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#1a202c'
            }}>
              <Star size={16} style={{ marginRight: '8px', color: '#ffa500' }} />
              Beta Feedback
            </h3>
            <p style={{ 
              margin: '0', 
              fontSize: '14px', 
              color: '#718096',
              lineHeight: '1.4'
            }}>
              Thanks for testing TravelFlow! Your feedback is sent via email to the TravelFlow team.
            </p>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            flexWrap: 'wrap',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <a
              href="mailto:dmilliorn@travelflow.link?subject=TravelFlow%20Beta%20Feedback"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '10px 16px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <MessageCircle size={14} />
              Send Feedback
            </a>
            
            <button
              onClick={() => setShowBugReport(!showBugReport)}
              style={{
                background: '#fef2f2',
                color: '#dc2626',
                padding: '10px 16px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                border: '1px solid #fecaca',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => e.target.style.background = '#fee2e2'}
              onMouseOut={(e) => e.target.style.background = '#fef2f2'}
            >
              <Bug size={14} />
              Report a Problem
            </button>
          </div>
          
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#f0fff4', 
            borderRadius: '6px',
            border: '1px solid #9ae6b4'
          }}>
            <p style={{ 
              margin: '0', 
              fontSize: '12px', 
              color: '#22543d',
              lineHeight: '1.4'
            }}>
              <strong>💡 What we're looking for:</strong> Bugs, missing features, confusing UI, 
              performance issues, or any suggestions to make TravelFlow better! Use "Report a Problem" 
              to fill out a detailed bug report form.
            </p>
          </div>
        </div>
      )}

      {/* Bug Report Form */}
      {showBugReport && (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderTop: 'none',
          padding: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          position: 'relative',
          zIndex: 999,
          width: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{ 
              margin: '0 0 8px 0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#1a202c'
            }}>
              <Bug size={16} style={{ marginRight: '8px', color: '#dc2626' }} />
              Report a Problem
            </h3>
            <p style={{ 
              margin: '0', 
              fontSize: '14px', 
              color: '#718096',
              lineHeight: '1.4'
            }}>
              Help us fix issues by providing detailed information. Your report will be sent via email to the TravelFlow team.
            </p>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '6px' 
              }}>
                Problem Description *
              </label>
              <textarea
                value={bugReport.description}
                onChange={(e) => setBugReport({...bugReport, description: e.target.value})}
                placeholder="Describe the problem you encountered..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '6px' 
              }}>
                Steps to Reproduce
              </label>
              <textarea
                value={bugReport.steps}
                onChange={(e) => setBugReport({...bugReport, steps: e.target.value})}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '6px' 
                }}>
                  Expected Behavior
                </label>
                <textarea
                  value={bugReport.expected}
                  onChange={(e) => setBugReport({...bugReport, expected: e.target.value})}
                  placeholder="What should have happened?"
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '6px' 
                }}>
                  Actual Behavior
                </label>
                <textarea
                  value={bugReport.actual}
                  onChange={(e) => setBugReport({...bugReport, actual: e.target.value})}
                  placeholder="What actually happened?"
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#374151', 
                marginBottom: '6px' 
              }}>
                Additional Notes
              </label>
              <textarea
                value={bugReport.additional}
                onChange={(e) => setBugReport({...bugReport, additional: e.target.value})}
                placeholder="Any other relevant information, screenshots, or context..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              marginTop: '8px'
            }}>
              <button
                onClick={() => setShowBugReport(false)}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
                onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitBugReport}
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? '#9ca3af' : '#dc2626',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseOver={(e) => {
                  if (!isSubmitting) e.target.style.background = '#b91c1c';
                }}
                onMouseOut={(e) => {
                  if (!isSubmitting) e.target.style.background = '#dc2626';
                }}
              >
                {isSubmitting ? 'Opening email...' : 'Send Bug Report'}
              </button>
            </div>
          </div>
          
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#fef3c7', 
            borderRadius: '6px',
            border: '1px solid #f59e0b'
          }}>
            <p style={{ 
              margin: '0', 
              fontSize: '12px', 
              color: '#92400e',
              lineHeight: '1.4'
            }}>
              <strong>🐛 Email Report:</strong> This will open your email client with the bug report 
              pre-filled. Review and send the email to complete the report.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default BetaBanner;
